import type { ISODate } from './types';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTHS_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));

export const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

const pad = (n: number): string => String(n).padStart(2, '0');

function fromISO(s: ISODate): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function toISO(d: Date): ISODate {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function todayISO(): ISODate {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
}

export function addMonths(year: number, monthIdx: number, delta: number) {
  const total = year * 12 + monthIdx + delta;
  return { year: Math.floor(total / 12), monthIdx: ((total % 12) + 12) % 12 };
}

// null cells pad the leading/trailing week so the grid is whole weeks.
export function monthGrid(year: number, monthIdx: number): (ISODate | null)[] {
  const startDow = new Date(Date.UTC(year, monthIdx, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate();
  const cells: (ISODate | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(toISO(new Date(Date.UTC(year, monthIdx, d))));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export function dayNumber(s: ISODate): number {
  return fromISO(s).getUTCDate();
}

export function monthTitle(year: number, monthIdx: number): string {
  return `${MONTH_NAMES[monthIdx]} ${year}`;
}

// 'yyyy-mm-dd' → 'Jan 26, 2026'. String math keeps it timezone-safe.
export function formatDateLabel(iso: ISODate): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${MONTHS_SHORT[m - 1]} ${d}, ${y}`;
}

export function viewOf(iso: ISODate): { year: number; monthIdx: number } {
  const [y, m] = iso.split('-').map(Number);
  return { year: y, monthIdx: m - 1 };
}

/** First and last day of a month, for asking a caller what it holds. */
export function monthBounds(year: number, monthIdx: number): [ISODate, ISODate] {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const lastDay = new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate();
  const month = pad(monthIdx + 1);
  return [`${year}-${month}-01`, `${year}-${month}-${pad(lastDay)}`];
}
